/**
 * HelpButton: the small question-mark button in a page header.
 *
 * Opens a pop-up with the summary of one help topic and a link to the full topic on the
 * Help page (`/help#<topic id>`). Used on the queue, the case and the dashboard.
 */

import { CircleQuestionMark, Icon } from "@norican/icons";
import { Button, Popover, Typography } from "@norican/react";
import { Link } from "react-router";

import { StatusLegend } from "@/components/figures/status-legend";
import { HELP_TOPIC_TRAINING } from "@/features/training/content";
import { trainingHref } from "@/features/training/player/progress";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { track } from "@/lib/usage";

import { helpTopic, type HelpTopicId } from "./content";

export const PRACTISE_THIS_TEXT = "Practise this in the training";

/** "Practise this in the training" for a topic with a lesson; null otherwise or when off. */
export function PractiseThisLink({ topic }: { topic: HelpTopicId }) {
  const target = FEATURES.training ? HELP_TOPIC_TRAINING[topic] : undefined;
  if (!target) {
    return null;
  }
  return (
    <Typography variant="body-sm">
      <Link
        to={trainingHref(target)}
        className="text-accent-fg-subtle font-semibold hover:underline"
        data-practise-this={topic}
      >
        {PRACTISE_THIS_TEXT}
      </Link>
    </Typography>
  );
}

/**
 * What the pop-up says under the summary: on the queue, the four status words with their
 * icons (the compact legend), so a sales person reads the icon in the Action column
 * without leaving the page.
 */
export function HelpPopoverExtras({ topic }: { topic: HelpTopicId }) {
  return (
    <>
      {topic === "queue" && <StatusLegend compact />}
      <Typography variant="body-sm">
        <Link to={`/help#${topic}`} className="text-accent-fg-subtle font-semibold hover:underline">
          Open the full help
        </Link>
      </Typography>
      <PractiseThisLink topic={topic} />
    </>
  );
}

export interface HelpButtonProps {
  topic: HelpTopicId;
}

export function HelpButton({ topic }: HelpButtonProps) {
  const entry = helpTopic(topic);
  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (open) {
          logger.debug("[help] pop-up", topic);
          track("help.topic", { topic, via: "popup" });
        }
      }}
    >
      <Popover.Trigger
        render={
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            iconOnly
            aria-label={`Help: ${entry.title}`}
            startIcon={<Icon icon={CircleQuestionMark} />}
          />
        }
      />
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6}>
          <Popover.Popup className="max-w-sm">
            <Popover.Header>
              <Popover.Title>{entry.title}</Popover.Title>
              <Popover.CloseButton label="Close help" />
            </Popover.Header>
            <Popover.Body className="flex flex-col gap-3">
              <Popover.Description>{entry.summary}</Popover.Description>
              <HelpPopoverExtras topic={topic} />
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
